import { ActivityIndicator, Modal, Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Button } from '@/components/ui/Button';
import { Caption, ThemedText } from '@/components/ui/Text';
import { AppImage } from '@/components/ui/AppImage';
import { useTheme } from '@/theme';
import type { TripSummary } from '@/services/tripSummary';
import { TripSummaryMapPreview } from '@/components/trips/TripSummaryMapPreview';
import { TripSummaryTopMoments } from '@/components/trips/TripSummaryTopMoments';
import type { ImageSourcePropType } from 'react-native';

type TripSummaryModalProps = {
  visible: boolean;
  summary: TripSummary;
  fallbackImageSource: ImageSourcePropType;
  onClose: () => void;
  onShare: () => void;
  sharePreparing?: boolean;
  showSharePreview?: boolean;
  sharePreviewUri?: string | null;
  onCancelSharePreview?: () => void;
  onConfirmShare?: () => void;
};

export function TripSummaryModal({
  visible,
  summary,
  fallbackImageSource,
  onClose,
  onShare,
  sharePreparing = false,
  showSharePreview = false,
  sharePreviewUri = null,
  onCancelSharePreview,
  onConfirmShare,
}: TripSummaryModalProps) {
  const theme = useTheme();
  const imageSource = summary.coverImage ? { uri: summary.coverImage } : fallbackImageSource;

  const handleClose = () => {
    if (sharePreparing) return;
    if (showSharePreview) {
      onCancelSharePreview?.();
      return;
    }
    onClose();
  };

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={handleClose}>
      <View style={{ flex: 1, backgroundColor: theme.colors.overlay, justifyContent: 'flex-end' }}>
        <View
          style={{
            maxHeight: showSharePreview ? '92%' : '88%',
            backgroundColor: theme.colors.surface,
            borderTopLeftRadius: theme.radii.xl,
            borderTopRightRadius: theme.radii.xl,
            overflow: 'hidden',
          }}
        >
          {showSharePreview ? (
            <View style={{ gap: theme.spacing(1), padding: theme.spacing(2) }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <View style={{ flex: 1, paddingRight: theme.spacing(1) }}>
                  <Caption color={theme.colors.textSecondary} style={{ textTransform: 'uppercase', fontWeight: '700' }}>
                    Share recap
                  </Caption>
                  <ThemedText variant="bodyStrong" numberOfLines={1}>
                    {summary.metrics.tripTitle}
                  </ThemedText>
                </View>
                <Pressable
                  onPress={handleClose}
                  accessibilityRole="button"
                  accessibilityLabel="Close recap preview"
                  style={{
                    padding: theme.spacing(0.75),
                    borderRadius: theme.radii.pill,
                    backgroundColor: theme.colors.backgroundSecondary,
                  }}
                >
                  <Ionicons name="close" size={18} color={theme.colors.text} />
                </Pressable>
              </View>

              <View
                style={{
                  borderRadius: theme.radii.lg,
                  overflow: 'hidden',
                  backgroundColor: theme.colors.backgroundSecondary,
                  minHeight: 420,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {sharePreparing ? (
                  <View style={{ alignItems: 'center', gap: theme.spacing(1), padding: theme.spacing(3) }}>
                    <ActivityIndicator color={theme.colors.primary} />
                    <Caption color={theme.colors.textSecondary}>Preparing your recap image...</Caption>
                  </View>
                ) : sharePreviewUri ? (
                  <AppImage
                    source={{ uri: sharePreviewUri }}
                    resizeMode="contain"
                    style={{ width: '100%', height: 520 }}
                  />
                ) : null}
              </View>

              <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: theme.spacing(1) }}>
                <Button label="Back" variant="ghost" onPress={handleClose} disabled={sharePreparing} />
                <Button
                  label="Share image"
                  onPress={onConfirmShare}
                  disabled={sharePreparing || !sharePreviewUri}
                />
              </View>
            </View>
          ) : (
            <>
              <View style={{ height: 170 }}>
                <AppImage source={imageSource} resizeMode="cover" style={{ width: '100%', height: '100%' }} />
                <View
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    backgroundColor: 'rgba(0,0,0,0.28)',
                    justifyContent: 'flex-end',
                    padding: theme.spacing(2),
                  }}
                >
                  <Caption style={{ color: '#FFFFFF', fontWeight: '700', textTransform: 'uppercase' }}>
                    Trip recap
                  </Caption>
                  <ThemedText variant="title" style={{ color: '#FFFFFF', marginTop: theme.spacing(0.25) }}>
                    {summary.metrics.tripTitle}
                  </ThemedText>
                </View>
                <Pressable
                  onPress={handleClose}
                  style={{
                    position: 'absolute',
                    top: theme.spacing(1),
                    right: theme.spacing(1),
                    padding: theme.spacing(0.75),
                    borderRadius: theme.radii.pill,
                    backgroundColor: 'rgba(0,0,0,0.42)',
                  }}
                  accessibilityRole="button"
                  accessibilityLabel="Close trip recap"
                >
                  <Ionicons name="close" size={18} color="#FFFFFF" />
                </Pressable>
              </View>
              <ScrollView contentContainerStyle={{ padding: theme.spacing(2), gap: theme.spacing(1.5) }}>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(1) }}>
                  {[
                    `${summary.metrics.durationDays} days`,
                    `${summary.metrics.placesVisitedCount} places`,
                    `${summary.collaboratorCount || 1} travelers`,
                    `${summary.checklist.completed}/${summary.checklist.total || 0} checklist`,
                  ].map((label) => (
                    <View
                      key={label}
                      style={{
                        paddingHorizontal: theme.spacing(1),
                        paddingVertical: theme.spacing(0.65),
                        borderRadius: theme.radii.pill,
                        backgroundColor: theme.colors.backgroundSecondary,
                        borderWidth: 1,
                        borderColor: theme.colors.borderMuted,
                      }}
                    >
                      <Caption style={{ fontWeight: '700' }}>{label}</Caption>
                    </View>
                  ))}
                </View>

                <TripSummaryMapPreview summary={summary} />
                <TripSummaryTopMoments moments={summary.topMoments} />

                <Button
                  label={sharePreparing ? 'Preparing recap...' : 'Share recap'}
                  onPress={onShare}
                  disabled={sharePreparing}
                />
              </ScrollView>
            </>
          )}
        </View>
      </View>
    </Modal>
  );
}
